import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, CircularProgress, List, ListItemAvatar, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode, $getSelection, $isRangeSelection, type TextNode } from 'lexical';
import type { TeamsPerson } from '@atlas/shared';

import { TeamsPeopleIcon, TeamsPersonIcon } from '@atlas/ui';
import { $createMentionNode, type MentionTarget } from './MentionNode';
import { TEAMS_MENTION_LIMIT, useTeamsPeople } from './useTeamsPeople';

/**
 * `@` plus at least one character. One character is the floor because `@` alone
 * fires on every email address and every "@ 3pm" someone types — the popup
 * would be in the way constantly for a list nobody asked for.
 */
const MENTION_MIN_QUERY_LENGTH = 1;
/**
 * A cap on how far past the `@` we keep matching. Beyond this the user is
 * plainly writing prose, not choosing a name, and the popup should get out of
 * the way rather than sit there matching nothing.
 */
const MENTION_MAX_QUERY_LENGTH = 40;

/**
 * One row of the popup: a person, or the conversation itself.
 *
 * The group option is not a fake person. It notifies through a different Graph
 * identity, it has no email and no avatar of its own, and giving it a
 * `TeamsPerson` shape would mean inventing an object id for something that has
 * none — which the create-chat path would then happily try to use.
 */
/**
 * Word-boundary prefix match, mirroring the server's rule for people.
 *
 * The same rule on both sides is the point: a group called "Action Steps (NFC
 * & Standards)" has to be findable by typing "standards", exactly as a person
 * is findable by their surname. A plain `startsWith` on the whole name would
 * only ever match "action".
 */
export function matchesMentionQuery(label: string, query: string | null): boolean {
  const needle = (query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const lower = label.toLowerCase();
  return [lower, ...lower.split(/[^a-z0-9]+/).filter(Boolean)].some((token) =>
    token.startsWith(needle),
  );
}

class MentionOption extends MenuOption {
  person?: TeamsPerson;
  target: MentionTarget;
  label: string;
  caption?: string;

  constructor(init: {
    key: string;
    label: string;
    target: MentionTarget;
    person?: TeamsPerson;
    caption?: string;
  }) {
    super(init.key);
    this.label = init.label;
    this.target = init.target;
    this.person = init.person;
    this.caption = init.caption;
  }
}

export interface TeamsMentionPluginProps {
  /**
   * Told whether the popup is open.
   *
   * The composer intercepts Enter in the CAPTURE phase — before the event ever
   * reaches Lexical — so the typeahead's own Enter handler can never run. This
   * is how the composer knows to stand down and let the menu take the key. It
   * is a signal, not a nicety: without it, choosing a person with Enter would
   * send the message instead.
   */
  onOpenChange: (isOpen: boolean) => void;
  disabled?: boolean;
  /**
   * The conversation being written in, so the popup offers ITS members.
   *
   * Teams scopes its own mention list this way, and the reason is not tidiness:
   * a mention of someone outside the chat renders as a name and notifies
   * nobody, so offering them is offering a silent failure.
   */
  chatId?: string;
  /**
   * The chat's name, present ONLY when it is a group — its presence is what
   * turns the group option on. The caller decides, because it is the side that
   * knows a one-to-one from a group.
   */
  groupMentionLabel?: string;
}

/**
 * Whether the menu will actually consume Enter.
 *
 * Not the same as "the menu is on screen". `LexicalMenu` declines the key when
 * it has no option to select, and the key then falls through to rich-text
 * handling — so reporting a matchless popup as open would turn Enter into a
 * line break instead of a send. Typing `@zzz` and pressing Enter must send the
 * message, exactly as it would with no plugin at all.
 */
export function claimsEnter(isMenuOpen: boolean, optionCount: number): boolean {
  return isMenuOpen && optionCount > 0;
}

/**
 * @mention autocomplete for the Teams composer.
 *
 * Built on `LexicalTypeaheadMenuPlugin`, which ships in the installed
 * `@lexical/react` (0.47) — verified before designing around it. It owns the
 * trigger detection, the arrow-key navigation and the text replacement; this
 * component supplies the people and the chrome.
 *
 * The people offered are the members of the chat being written in. ATLAS cannot
 * search the tenant directory (`User.ReadBasic.All` is not consented), so the
 * roster comes from the conversation itself. Typing a name that produces
 * nothing therefore leaves the plain text exactly as written, which is the
 * right outcome: the message still says what the user meant, it simply does not
 * notify anyone.
 */
export function TeamsMentionPlugin({
  onOpenChange,
  disabled = false,
  chatId,
  groupMentionLabel,
}: TeamsMentionPluginProps) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = !disabled && query !== null;

  const { people, isLoading, isDebouncing } = useTeamsPeople({
    query: query ?? '',
    enabled: isActive,
    limit: TEAMS_MENTION_LIMIT,
    minLength: MENTION_MIN_QUERY_LENGTH,
    chatId,
  });

  const triggerFn = useBasicTypeaheadTriggerMatch('@', {
    minLength: MENTION_MIN_QUERY_LENGTH,
    maxLength: MENTION_MAX_QUERY_LENGTH,
  });

  /**
   * The group sits FIRST, and only in a group chat.
   *
   * First because it is the one option whose reach is everyone, so burying it
   * under six names would make the loudest thing in the menu the hardest to
   * find — Teams orders it the same way. Only in a group chat because mentioning
   * "the conversation" in a one-to-one is a second way to ping the one person
   * already sitting at the top of the list.
   *
   * It is FILTERED by the query like any other row. An always-present option
   * would quietly break the rule the plugin already keeps: typing `@zzz` and
   * pressing Enter must SEND the message, and it only does that while the menu
   * has nothing to select. A group row that always matches would turn that
   * keystroke into a group ping nobody asked for.
   */
  const options = useMemo(() => {
    const rows = people.map(
      (person) =>
        new MentionOption({
          key: person.userId,
          label: person.displayName,
          caption: person.email,
          target: { kind: 'user', userId: person.userId },
          person,
        }),
    );

    if (!groupMentionLabel || !chatId || !matchesMentionQuery(groupMentionLabel, query)) {
      return rows;
    }

    return [
      new MentionOption({
        key: `conversation:${chatId}`,
        label: groupMentionLabel,
        caption: t('teams.panel.mentionEveryone'),
        target: { kind: 'conversation', conversationId: chatId },
      }),
      ...rows,
    ];
  }, [people, groupMentionLabel, chatId, query, t]);

  // Closed is the only safe default, so the composer's Enter-sends behaviour is
  // what survives an unmount, a disable, or a query that matched nobody.
  useEffect(() => {
    onOpenChange(!disabled && isActive && claimsEnter(isMenuOpen, options.length));
  }, [disabled, isActive, isMenuOpen, options.length, onOpenChange]);

  useEffect(() => () => onOpenChange(false), [onOpenChange]);

  const onSelectOption = useCallback(
    (
      option: MentionOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mention = $createMentionNode(`@${option.label}`, option.target);
        if (nodeToReplace) {
          nodeToReplace.replace(mention);
        } else {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.insertNodes([mention]);
        }
        // A trailing space, because the mention is a token: without it the
        // caret sits against an uneditable node and the next word has nowhere
        // to land. Selecting the space puts the caret after it.
        const space = $createTextNode(' ');
        mention.insertAfter(space);
        space.select();
      });
      closeMenu();
    },
    [editor],
  );

  const isBusy = isLoading || isDebouncing;

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      options={options}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!anchorElementRef.current || disabled) return null;
        // Nothing to show and nothing in flight: render no popup at all rather
        // than an empty box floating over the message being written.
        if (!isBusy && options.length === 0) return null;

        return createPortal(
          <Paper
            elevation={8}
            /**
             * OPENS UPWARD, and it has to.
             *
             * Lexical anchors this at the caret and lets it flow downward — but
             * the caret is in a composer pinned to the BOTTOM of the panel, so
             * downward is off the end of the window. Measured on a 907px-tall
             * viewport: the menu ran 874 → 950, putting a single result 43px
             * past the fold and six results almost entirely out of sight. It
             * read as "@ does nothing", because the only visible evidence was a
             * sliver of one row behind the hint text.
             *
             * `bottom: 100%` on an absolutely-positioned child flips it above
             * the caret, over the thread — which is where Teams puts its own.
             * Above is always the roomier side here: the composer sits at the
             * bottom of a full-height panel, so whatever space exists is above
             * it.
             *
             * The panel floors at 280px and this floats inside it, so the width
             * is bounded by content rather than a fixed measure.
             */
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              mb: 0.5,
              /**
               * ABOVE THE SIDEBAR PANEL, and this is not optional.
               *
               * Lexical portals the anchor to `<body>` with no z-index, so the
               * menu stacked at the document floor. On the standalone
               * `/teams-chat` page nothing competes and it was visible — but in
               * the sidebar the panel is a `position: fixed` Paper at
               * `zIndex.drawer` (1200), which painted straight over it. The
               * menu was rendering, in the right place, completely hidden. That
               * is why @ appeared to work popped out and not in the sidebar.
               *
               * `tooltip` rather than `drawer + 1`: this is a transient layer
               * over whatever is beneath it, and pinning it one step above one
               * particular surface would break again the moment it opens over
               * another.
               */
              zIndex: (theme) => theme.zIndex.tooltip,
              width: 'max-content',
              maxWidth: 'min(320px, 90vw)',
              minWidth: 180,
              overflow: 'hidden',
            }}
          >
            {isBusy ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={18} aria-label={t('teams.panel.loading')} />
              </Box>
            ) : (
              <List
                role="listbox"
                aria-label={t('teams.panel.mentionResultsLabel')}
                dense
                disablePadding
                sx={{ maxHeight: 220, overflowY: 'auto' }}
              >
                {options.map((option, index) => (
                  <ListItemButton
                    key={option.key}
                    ref={(element: HTMLElement | null) => option.setRefElement(element)}
                    role="option"
                    aria-selected={index === selectedIndex}
                    selected={index === selectedIndex}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => {
                      setHighlightedIndex(index);
                      selectOptionAndCleanUp(option);
                    }}
                    sx={{ minWidth: 0 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 34 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {option.target.kind === 'conversation' ? (
                          <TeamsPeopleIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <TeamsPersonIcon sx={{ fontSize: 14 }} />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={option.label}
                      secondary={option.caption}
                      slotProps={{
                        primary: { noWrap: true, variant: 'body2' },
                        secondary: { noWrap: true, variant: 'caption' },
                      }}
                      sx={{ minWidth: 0, my: 0 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 1.5, py: 0.5, borderTop: 1, borderColor: 'divider' }}
            >
              {t('teams.panel.mentionScopeHint')}
            </Typography>
          </Paper>,
          anchorElementRef.current,
        );
      }}
    />
  );
}
